import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Loader2, RefreshCw, Save, Sparkles, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { renderToStaticMarkup } from 'react-dom/server'
import TurndownService from 'turndown'
// @ts-expect-error — turndown-plugin-gfm n'a pas de types officiels mais c'est trivial
import { gfm as turndownGfm } from 'turndown-plugin-gfm'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card } from '@/components/ui/card'
import type {
  KnowledgePage,
  KnowledgePageCreateInput,
  KnowledgePagePreview,
  KnowledgePageUpdateInput,
} from '@/api/knowledgePages'

// =============================================================================
// Éditeur WYSIWYG : on rend le markdown en HTML, on le rend contentEditable,
// et au moment du save on reconvertit le HTML en markdown via Turndown.
//
// L'utilisateur ne voit JAMAIS de markdown brut — il édite directement le
// contenu rendu (titres, listes, tableaux), comme dans Notion ou Google Docs.
// =============================================================================

type EditorMode = 'preview' | 'edit'

interface Props {
  initial: KnowledgePagePreview | KnowledgePage
  mode: EditorMode
  onSave: (input: KnowledgePageCreateInput | KnowledgePageUpdateInput) => Promise<void>
  onCancel: () => void
  onDelete?: () => Promise<void>
  onRescrape?: () => Promise<void>
  /**
   * Nettoyage IA : reçoit le markdown courant, retourne la version compactée
   * (rien n'est sauvegardé). L'éditeur remplace le contenu visible ;
   * l'utilisateur clique Enregistrer pour persister. Permet le rollback
   * (cliquer Retour sans sauver).
   *
   * Disponible en preview ET edit : l'utilisateur peut nettoyer juste après
   * un scrape, ou plus tard sur une page existante.
   */
  onCleanWithAI?: (currentMarkdown: string) => Promise<{ cleaned: string; ratio: number } | null>
  isSaving?: boolean
  isRescraping?: boolean
  isCleaning?: boolean
}

// Instance unique de Turndown (HTML→Markdown), partagée entre tous les renders.
const turndownService = new TurndownService({
  headingStyle: 'atx', // # H1 plutôt que H1\n===
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
  emDelimiter: '*',
  strongDelimiter: '**',
})
turndownService.use(turndownGfm) // active les tableaux Markdown

/**
 * Convertit le markdown initial en HTML qu'on pourra rendre contentEditable.
 * On utilise React lui-même pour faire le rendu, puis on serialize en HTML.
 *
 * Les images sont volontairement supprimées (renvoie null) : l'agent vocal IA
 * ne « voit » pas les images, donc on n'a pas besoin de les afficher dans
 * l'éditeur non plus. Filet de sécurité si le backend a laissé passer un
 * `![alt](url)` malgré le strip_images_from_markdown côté Python.
 */
function markdownToHtml(markdown: string): string {
  return renderToStaticMarkup(
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ img: () => null }}>
      {markdown}
    </ReactMarkdown>,
  )
}

function coerceContentToString(raw: unknown): string {
  if (typeof raw === 'string') return raw
  if (raw == null) return ''
  try {
    return JSON.stringify(raw, null, 2)
  } catch {
    return String(raw)
  }
}

export function KnowledgePageEditor({
  initial,
  mode,
  onSave,
  onCancel,
  onDelete,
  onRescrape,
  onCleanWithAI,
  isSaving = false,
  isRescraping = false,
  isCleaning = false,
}: Props) {
  const { t } = useTranslation()
  const tkb = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`manager.aiAgents.${key}`, { defaultValue: fallback, ...(opts ?? {}) })

  const [title, setTitle] = useState(initial.title || '')
  const [enabled, setEnabled] = useState<boolean>(
    'enabled' in initial ? initial.enabled : true,
  )

  // Markdown source — on le garde en state pour pouvoir le re-render quand
  // l'utilisateur fait Re-scraper ou si initial change.
  const initialContent = useMemo(() => coerceContentToString(initial.content), [initial])
  const [markdown, setMarkdown] = useState<string>(initialContent)

  // HTML rendu initial — on l'injecte une seule fois dans le contentEditable.
  // Ensuite le DOM contentEditable devient la source de vérité jusqu'au save.
  const initialHtml = useMemo(() => markdownToHtml(markdown), [markdown])

  const editorRef = useRef<HTMLDivElement>(null)

  // Quand le contenu source change (cas Re-scraper), on remplace l'HTML
  // dans le contentEditable. Sinon on laisse l'utilisateur éditer librement.
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialHtml
    }
  }, [initialHtml])

  const sourceUrl = initial.source_url

  const handleCleanWithAI = async () => {
    if (!onCleanWithAI) return
    // Récupère le markdown courant : si l'utilisateur a édité dans le
    // contentEditable, on prend cet état ; sinon on tombe sur le markdown
    // initial. Même logique que handleSave.
    let currentMarkdown = markdown
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      currentMarkdown = turndownService.turndown(html).trim()
    }
    if (!currentMarkdown.trim()) {
      toast.error(tkb('kbToastNoContentToClean', 'Aucun contenu à nettoyer'))
      return
    }
    const result = await onCleanWithAI(currentMarkdown)
    if (!result) return
    // Remplace l'éditeur visible par le contenu nettoyé. Le user voit
    // immédiatement le résultat ; il peut éditer puis cliquer Enregistrer
    // (ou Ajouter la page en mode preview) pour persister, ou Retour pour annuler.
    setMarkdown(result.cleaned)
    if (editorRef.current) {
      editorRef.current.innerHTML = markdownToHtml(result.cleaned)
    }
    const verb =
      mode === 'preview'
        ? tkb('kbEditorAddPage', 'Ajouter la page')
        : tkb('kbEditorSave', 'Enregistrer')
    toast.success(
      tkb('kbToastCleanSuccess', 'Page nettoyée (réduite {{ratio}}×). Cliquez « {{verb}} » pour appliquer.', {
        ratio: result.ratio.toFixed(1),
        verb,
      }),
    )
  }

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(tkb('kbToastTitleRequired', 'Le titre est obligatoire'))
      return
    }

    // Récupère l'HTML édité par l'utilisateur, le reconvertit en markdown.
    let editedMarkdown = markdown
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      editedMarkdown = turndownService.turndown(html).trim()
    }

    try {
      await onSave({
        title: title.trim(),
        source_url: sourceUrl || undefined,
        content: editedMarkdown,
        enabled,
        raw_scraped_text:
          mode === 'preview' && 'raw_scraped_text' in initial
            ? initial.raw_scraped_text
            : undefined,
      })
      setMarkdown(editedMarkdown) // synchro l'état local avec le sauvegardé
      toast.success(
        mode === 'preview'
          ? tkb('kbToastPageAdded', 'Page de connaissances ajoutée')
          : tkb('kbToastChangesSaved', 'Modifications enregistrées'),
      )
    } catch {
      toast.error(tkb('kbToastSaveFailed', 'Échec de la sauvegarde'))
    }
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-slate-900">
      {/* Header sticky */}
      <div className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isSaving}
            className="gap-2 flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{tkb('kbEditorBack', 'Retour')}</span>
          </Button>
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold truncate">
              {mode === 'preview'
                ? tkb('kbEditorPreviewTitle', 'Aperçu de la page')
                : title || tkb('kbEditorDefaultTitle', 'Page de connaissances')}
            </h2>
            {sourceUrl && (
              <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                {sourceUrl}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {mode === 'edit' && sourceUrl && onRescrape && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRescrape}
              disabled={isSaving || isRescraping || isCleaning}
              className="gap-2"
            >
              {isRescraping ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{tkb('kbEditorRescrape', 'Re-scraper')}</span>
            </Button>
          )}
          {onCleanWithAI && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCleanWithAI}
              disabled={isSaving || isRescraping || isCleaning}
              className="gap-2"
              title={tkb('kbEditorCleanWithAiTooltip', "Compacte le contenu pour un agent vocal (supprime marketing, navigation, doublons ; garde tarifs et faits).")}
            >
              {isCleaning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{tkb('kbEditorCleanWithAi', "Nettoyer avec l'IA")}</span>
            </Button>
          )}
          {mode === 'edit' && onDelete && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDelete}
              disabled={isSaving}
              className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">{tkb('kbEditorDelete', 'Supprimer')}</span>
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {mode === 'preview'
              ? tkb('kbEditorAddPage', 'Ajouter la page')
              : tkb('kbEditorSave', 'Enregistrer')}
          </Button>
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
          {/* Titre + toggle Activée */}
          <Card className="p-4 space-y-3 bg-white dark:bg-slate-900 border dark:border-slate-800">
            <div>
              <Label htmlFor="kp-title" className="text-xs text-gray-500">
                {tkb('kbEditorTitleLabel', 'Titre')}
              </Label>
              <Input
                id="kp-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={tkb('kbEditorTitlePlaceholder', 'Ex : Tarifs et options')}
                className="mt-1.5"
              />
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t dark:border-slate-700">
              <div className="min-w-0">
                <Label htmlFor="kp-enabled" className="cursor-pointer">
                  {tkb('kbEditorEnabled', 'Page activée')}
                </Label>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                  {tkb('kbEditorEnabledHelp', "Si désactivée, cette page n'est pas envoyée à l'agent IA.")}
                </p>
              </div>
              <Switch
                id="kp-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </Card>

          {/* Éditeur WYSIWYG — contentEditable sur le markdown rendu */}
          <Card className="p-0 overflow-hidden bg-white dark:bg-slate-900 border dark:border-slate-800">
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              spellCheck
              className="prose prose-sm sm:prose dark:prose-invert max-w-none p-5 sm:p-8 min-h-[400px] focus:outline-none"
              // Le contenu initial est injecté via useEffect (cf. plus haut)
              // pour gérer les re-scrapes sans perdre les éditions locales.
            />
          </Card>

          <p className="text-xs text-gray-500 dark:text-slate-400 px-1">
            {tkb('kbEditorHint', 'Édite directement le texte ci-dessus comme dans un document. Les modifications sont enregistrées au clic sur « {{action}} ».', {
              action: mode === 'preview'
                ? tkb('kbEditorAddPage', 'Ajouter la page')
                : tkb('kbEditorSave', 'Enregistrer'),
            })}
          </p>
        </div>
      </div>
    </div>
  )
}
